import React from "react";

/**
 * The allergy banner (build plan §7.1 #3): on EVERY person screen, at the top,
 * before anything else about the person. Medication allergies are named
 * separately (Decisions 2 #22). With the M3 Stage 3 panel (allergy-panel.ts)
 * it also shows the allergies as confirmed at the med station, the allergy
 * response note (what to do if exposed, where the rescue medication is) and
 * whether an allergy / anaphylaxis action plan is on file. Medication
 * allergies sit apart, first, under their own MEDICATION ALLERGY tag in
 * another colour, so the giver cannot miss them (owner walk-through
 * 2026-10-18; the same rule as the server's medicationAllergiesOf).
 */

export interface AllergyBannerData {
  state: "listed" | "none" | "unknown";
  allergies: { allergy: string; reaction: string; category: string; anaphylaxisRisk: "Yes" | "No" | null; bringingEpiPen: "Yes" | "No" | null }[];
  medicationAllergy: string | null;
}

export interface AllergyResponseData {
  noteKey: string;
  allergens: string;
  reactionHistory: string;
  severity: string;
  rescueMedication: string;
  rescueLocation: string;
  steps: string;
  whoToCall: string;
  authorName: string;
  authorEmail: string;
  createdUtc: string;
  earlier: number;
}

export interface AllergyPanelData extends AllergyBannerData {
  confirmed?: { rows: { allergy: string; reaction: string; category: string; anaphylaxisRisk: string; bringingEpiPen: string }[]; medicationAllergy: string } | null;
  response?: AllergyResponseData | null;
  plans?: { docKey: string; fileName: string; uploadedAtUtc: string; uploadedByEmail: string; source?: "med" | "portal"; href?: string }[];
}

/** Where a plan on file opens: the server says (an attached plan, or the family's portal upload). */
export function planHref(p: { docKey: string; href?: string }): string {
  return p.href || `/api/med-documents/${encodeURIComponent(p.docKey)}`;
}

const isMedicationCategory = (c: string | undefined) => /^medication/i.test((c ?? "").trim());

/** The medication allergies: Medication-category rows (confirmed if confirmed), then the follow-up answer. */
export function medicationAllergiesOf(d: AllergyPanelData): string[] {
  const rows = d.confirmed?.rows.length ? d.confirmed.rows : d.allergies;
  const out = rows.filter((a) => a.allergy && isMedicationCategory(a.category)).map((a) => `${a.allergy}${a.reaction ? ` (${a.reaction})` : ""}`);
  const followUp = d.confirmed?.medicationAllergy || d.medicationAllergy || "";
  if (followUp && !out.some((x) => x.toLowerCase().includes(followUp.toLowerCase()))) out.push(followUp);
  return out;
}

/** The tag itself: the pass's collapsed rows use it too. */
export function MedicationAllergyTag() {
  return <span className="rounded bg-violet-800 px-1.5 py-0.5 text-xs font-bold uppercase tracking-wide text-white">Medication allergy</span>;
}

function MedicationAllergies({ data }: { data: AllergyPanelData }) {
  const med = medicationAllergiesOf(data);
  if (!med.length) return null;
  return (
    <div className="mb-2 rounded border-2 border-violet-700 bg-violet-50 px-3 py-2 text-violet-950" role="alert" aria-label="Medication allergy">
      <MedicationAllergyTag />
      <ul className="mt-1 space-y-0.5">{med.map((m) => <li key={m} className="font-semibold">{m}</li>)}</ul>
    </div>
  );
}

export const RESCUE_LOCATION_LABEL: Record<string, string> = { MedStation: "Med station", SelfCarry: "Self-carry", WithCounselor: "With counselor" };

const when = (iso: string) => {
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? "" : new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(t);
};

/** The response note and the plan, shown inside the banner. */
function Extras({ data }: { data: AllergyPanelData }) {
  const r = data.response;
  const plans = data.plans ?? [];
  if (!r && !plans.length && !data.confirmed) return null;
  return (
    <div className="mt-2 space-y-1 border-t border-red-200 pt-2">
      {data.confirmed && data.confirmed.rows.some((a) => !isMedicationCategory(a.category)) && (
        <p><span className="font-semibold">Confirmed at the med station:</span> {data.confirmed.rows.filter((a) => !isMedicationCategory(a.category)).map((a) => `${a.allergy}${a.anaphylaxisRisk === "Yes" ? " (anaphylaxis risk)" : ""}`).join("; ")}</p>
      )}
      {r && (
        <div>
          <p className="font-semibold">
            Allergy response{r.severity ? ` — ${r.severity.toUpperCase()}` : ""}
            <span className="ml-1 font-normal text-red-900">({r.authorName || r.authorEmail}, {when(r.createdUtc)})</span>
          </p>
          {r.allergens && <p>Allergens: {r.allergens}</p>}
          {r.rescueMedication && <p>Rescue medication: {r.rescueMedication}{r.rescueLocation ? ` — where: ${RESCUE_LOCATION_LABEL[r.rescueLocation] ?? r.rescueLocation}` : ""}</p>}
          {r.steps && <p className="whitespace-pre-wrap">If exposed: {r.steps}</p>}
          {r.whoToCall && <p>Call: {r.whoToCall}</p>}
        </div>
      )}
      {plans.length > 0 ? (
        <p>
          <span className="font-semibold">Action plan on file{plans[0].source === "portal" ? " (the family's Step 3 upload)" : ""}:</span>{" "}
          <a className="underline" href={planHref(plans[0])} target="_blank" rel="noreferrer">open</a>
        </p>
      ) : (
        (r?.severity === "Anaphylaxis" || data.allergies.some((a) => a.anaphylaxisRisk === "Yes")) && <p className="text-red-900">No allergy action plan on file.</p>
      )}
    </div>
  );
}

export function AllergyBanner({ data }: { data: AllergyPanelData }): React.ReactElement {
  if (data.state === "none" && !data.response && !(data.confirmed?.rows.length)) {
    return (
      <div className="mb-4 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700" role="note" aria-label="Allergies">
        <span className="font-semibold">Allergies:</span> none listed by the family.
      </div>
    );
  }
  if (data.state === "unknown" && !data.response && !(data.confirmed?.rows.length)) {
    return (
      <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-950" role="note" aria-label="Allergies">
        <span className="font-semibold">Allergies:</span> not answered in the application. Confirm with the family.
      </div>
    );
  }
  const severe = data.allergies.some((a) => a.anaphylaxisRisk === "Yes") || data.response?.severity === "Anaphylaxis";
  const others = data.allergies.filter((a) => !isMedicationCategory(a.category));
  return (
    <div className="mb-4 rounded-md border-2 border-red-600 bg-red-50 px-4 py-3 text-sm text-red-950" role="alert" aria-label="Allergies">
      <MedicationAllergies data={data} />
      <p className="font-bold uppercase tracking-wide">
        {medicationAllergiesOf(data).length ? "Other allergies" : "Allergies"}{severe ? " — anaphylaxis risk" : ""}
      </p>
      <ul className="mt-1 space-y-0.5">
        {!others.length && <li>None other listed.</li>}
        {others.map((a, i) => (
          <li key={i}>
            <span className="font-semibold">{a.allergy}</span>
            {a.category ? ` (${a.category.toLowerCase()})` : ""}
            {a.reaction ? ` — ${a.reaction}` : ""}
            {a.anaphylaxisRisk === "Yes" ? " · ANAPHYLAXIS RISK" : ""}
            {a.bringingEpiPen === "Yes" ? " · bringing an epinephrine auto-injector" : ""}
          </li>
        ))}
      </ul>
      <Extras data={data} />
    </div>
  );
}
